import { resolveApiBaseUrl } from "./map-runtime-urls.js";
import {
  QUERY_MATCH_RANK_NONE,
  compactQueryMatchText,
  matchQueryValues,
  normalizeQueryMatchText,
  queryMatchTokens,
} from "../js/query/match.js";

const DEFAULT_ZONE_CATALOG_PATH = "/api/v1/zones";

function hasOwnKey(object, key) {
  return !!object && Object.prototype.hasOwnProperty.call(object, key);
}

function rgbTripletToU32(r, g, b) {
  return (((r & 0xff) << 16) | ((g & 0xff) << 8) | (b & 0xff)) >>> 0;
}

function parseCatalogRgbByte(value) {
  const number = Number.parseFloat(value);
  if (!Number.isFinite(number)) {
    return null;
  }
  if (number >= 0 && number <= 255 && Math.abs(number - Math.round(number)) < 1e-6) {
    return Math.round(number);
  }
  return null;
}

function parseCatalogInteger(value) {
  const number = Number.parseInt(value, 10);
  return Number.isFinite(number) ? number : null;
}

function parseCatalogBoolean(value) {
  if (value === true || value === false) {
    return value;
  }
  if (value === 1 || value === 0) {
    return value === 1;
  }
  const normalized = String(value ?? "").trim().toLowerCase();
  if (normalized === "true" || normalized === "1") {
    return true;
  }
  if (normalized === "false" || normalized === "0") {
    return false;
  }
  return null;
}

function exactRgbConfirmationOrder(value) {
  if (value === true) {
    return 0;
  }
  if (value === false) {
    return 1;
  }
  return 2;
}

function formatRgbKey(r, g, b, separator = ",") {
  return `${r}${separator}${g}${separator}${b}`;
}

function formatNormalizedRgbComponent(value) {
  return (value / 255).toFixed(6);
}

export function parseZoneRgbSearch(searchText) {
  const query = String(searchText || "").trim().toLowerCase();
  if (!query) {
    return null;
  }

  const compactQuery = query.replace(/\s+/g, "");
  const hexMatch = compactQuery.match(/^(?:#|0x)?([0-9a-f]{6})$/i);
  if (hexMatch) {
    return Number.parseInt(hexMatch[1], 16);
  }

  const sanitized = query.replace(/\b(?:rgb|rgba|vec3|vec4|normalized|norm|color|zone)\b/g, " ");
  const components = sanitized.match(/[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?/g) || [];
  if (components.length !== 3 && components.length !== 4) {
    return null;
  }
  const remainder = sanitized
    .replace(/[+-]?(?:\d+\.?\d*|\.\d+)(?:e[+-]?\d+)?/g, "")
    .replace(/[\s,;:/()[\]{}]+/g, "");
  if (remainder) {
    return null;
  }

  const values = components.slice(0, 3).map((value) => Number.parseFloat(value));
  if (values.some((value) => !Number.isFinite(value) || value < 0)) {
    return null;
  }
  const normalized =
    values.every((value) => value <= 1) &&
    components
      .slice(0, 3)
      .some((value) => value.includes(".") || value.toLowerCase().includes("e"));
  const bytes = normalized
    ? values.map((value) => Math.round(value * 255))
    : values.map((value) =>
        value <= 255 && Math.abs(value - Math.round(value)) < 1e-6 ? Math.round(value) : null,
      );
  if (bytes.some((value) => !Number.isInteger(value) || value < 0 || value > 255)) {
    return null;
  }
  return rgbTripletToU32(bytes[0], bytes[1], bytes[2]);
}

function zoneTextMatch(zone, rawQuery) {
  return matchQueryValues(
    [
      zone?.name,
      zone?._nameSearch,
    ],
    rawQuery,
  );
}

function scoreZoneMatch(zone, parsedZoneRgb, rawQuery = "") {
  if (parsedZoneRgb != null && zone.zoneRgb === parsedZoneRgb) {
    return {
      rank: QUERY_MATCH_RANK_NONE,
      score: 500,
      matchMode: "rgb-exact",
      matchedValue: String(zone.rgbKey || zone.zoneRgb || ""),
      windowStart: null,
      windowEnd: null,
    };
  }
  return zoneTextMatch(zone, rawQuery);
}

export function normalizeZoneCatalog(rawCatalog) {
  const entries = Array.isArray(rawCatalog)
    ? rawCatalog
    : Array.isArray(rawCatalog?.zones)
      ? rawCatalog.zones
      : [];
  const normalized = [];
  for (const entry of entries) {
    const r = parseCatalogRgbByte(entry?.r ?? entry?.rgb?.r);
    const g = parseCatalogRgbByte(entry?.g ?? entry?.rgb?.g);
    const b = parseCatalogRgbByte(entry?.b ?? entry?.rgb?.b);
    if (![r, g, b].every(Number.isInteger)) {
      continue;
    }
    const zoneRgb = rgbTripletToU32(r, g, b);
    const rgbKey = formatRgbKey(r, g, b);
    const normalizedParts = [
      formatNormalizedRgbComponent(r),
      formatNormalizedRgbComponent(g),
      formatNormalizedRgbComponent(b),
    ];
    const hex = Number(zoneRgb).toString(16).padStart(6, "0");
  const name = String(entry?.name || "").trim() || `Zone ${rgbKey}`;
    const exactRgbConfirmed = parseCatalogBoolean(entry?.exactRgbConfirmed);
    const active = parseCatalogBoolean(entry?.active);
    const order = parseCatalogInteger(entry?.order ?? entry?.index);
    const biteTimeMin = parseCatalogInteger(entry?.biteTimeMin ?? entry?.bite_time_min);
    const biteTimeMax = parseCatalogInteger(entry?.biteTimeMax ?? entry?.bite_time_max);
    normalized.push({
      kind: "zone",
      zoneRgb,
      r,
      g,
      b,
      name,
      exactRgbConfirmed,
      active,
      order: Number.isFinite(order) ? order : Number.MAX_SAFE_INTEGER,
      biteTimeMin,
      biteTimeMax,
      rgbKey,
      rgbSpaced: formatRgbKey(r, g, b, " "),
      normalizedKey: normalizedParts.join(","),
      normalizedSpaced: normalizedParts.join(" "),
      hexKey: `0x${hex}`,
      hashHexKey: `#${hex}`,
      bareHexKey: hex,
      _nameSearch: normalizeQueryMatchText(name),
      _nameSearchCompact: compactQueryMatchText(name),
    });
  }
  normalized.sort((left, right) => {
    if (left.order !== right.order) {
      return left.order - right.order;
    }
    const confirmationOrder =
      exactRgbConfirmationOrder(left.exactRgbConfirmed) -
      exactRgbConfirmationOrder(right.exactRgbConfirmed);
    if (confirmationOrder !== 0) {
      return confirmationOrder;
    }
    return String(left.name || "").localeCompare(String(right.name || ""));
  });
  return normalized;
}

export function findZoneMatches(zoneCatalog, searchText) {
  const query = normalizeQueryMatchText(searchText);
  if (!query) {
    return [];
  }
  const parsedZoneRgb = parseZoneRgbSearch(query);
  const queryTokens = parsedZoneRgb == null ? queryMatchTokens(query) : [];
  const filtered = [];
  for (const zone of zoneCatalog || []) {
    const match = scoreZoneMatch(zone, parsedZoneRgb, query);
    if (match.rank <= QUERY_MATCH_RANK_NONE && match.matchMode !== "rgb-exact") {
      continue;
    }
    filtered.push({
      ...zone,
      _score: match.score,
      _matchRank: match.rank,
      _matchMode: match.matchMode,
      _matchedValue: match.matchedValue,
      _matchWindowStart: Number.isInteger(match.windowStart) ? match.windowStart : 0,
      _matchWindowEnd: Number.isInteger(match.windowEnd) ? match.windowEnd : 0,
      _queryMatch: match,
    });
  }
  const bestMatchRank = queryTokens.length > 1 && filtered.length
    ? Math.max(...filtered.map((candidate) => candidate._matchRank))
    : null;
  const ranked = Number.isInteger(bestMatchRank)
    ? filtered.filter((zone) => zone._matchRank === bestMatchRank)
    : filtered;
  ranked.sort((left, right) => {
    if (right._score !== left._score) {
      return right._score - left._score;
    }
    const confirmationOrder =
      exactRgbConfirmationOrder(left.exactRgbConfirmed) -
      exactRgbConfirmationOrder(right.exactRgbConfirmed);
    if (confirmationOrder !== 0) {
      return confirmationOrder;
    }
    if (left.order !== right.order) {
      return left.order - right.order;
    }
    return String(left.name || "").localeCompare(String(right.name || ""));
  });
  return ranked;
}

export async function loadZoneCatalog(
  fetchImpl = globalThis.fetch,
  locationLike = globalThis.window?.location,
) {
  if (typeof fetchImpl !== "function") {
    return [];
  }
  const apiBaseUrl = resolveApiBaseUrl({ locationLike });
  const url = `${apiBaseUrl}${DEFAULT_ZONE_CATALOG_PATH}`;
  try {
    const response = await fetchImpl(url);
    if (!response?.ok) {
      throw new Error(`zone catalog request failed with status ${response?.status ?? "unknown"}`);
    }
    return normalizeZoneCatalog(await response.json());
  } catch (error) {
    console.warn("Failed to load zone search catalog", error);
    return [];
  }
}

function zoneRgbFromCatalogLookupInput(value) {
  if (!Array.isArray(value)) {
    return Number.parseInt(value, 10);
  }
  if (value.length !== 3) {
    return null;
  }
  const channels = value.map(parseCatalogRgbByte);
  return channels.every((channel) => channel != null)
    ? rgbTripletToU32(channels[0], channels[1], channels[2])
    : null;
}

export function zoneCatalogEntryForRgb(zoneCatalog, zoneRgbInput) {
  const zoneRgb = zoneRgbFromCatalogLookupInput(zoneRgbInput);
  if (!Number.isFinite(zoneRgb)) {
    return null;
  }
  return (Array.isArray(zoneCatalog) ? zoneCatalog : []).find((zone) => zone.zoneRgb === zoneRgb) || null;
}

export function zoneDisplayNameFromCatalog(zoneCatalog, zoneRgbInput) {
  const zone = zoneCatalogEntryForRgb(zoneCatalog, zoneRgbInput);
  if (!zone) {
    return "";
  }
  return String(zone.name || "").trim();
}
